import * as React from 'react';
import styled from '@emotion/styled';
import {
    PropertyWrapper,
    StyledFeaturePropertiesHeader,
    StyledBadge
} from './FeaturedProperties.styles';
import FeaturedPropertyCard from './FeaturedPropertyCard';
import {
    internalPromos,
    internalPromoIds
} from './InternalPromoCard/internalPromoData';
import InternalPromoCard from './InternalPromoCard';

type InternalPromoId = typeof internalPromoIds[number];
type PromoIds = typeof internalPromoIds[number];

type Product =
    | {
          listingId: string;
          featuredType?: string | null;
          title?: string | null;
          price?: string | null;
          alternativeRentFrequencyLabel?: string | null;
          address?: string | null;
          branch?: {
              logoUrl?: string | null;
              name?: string | null;
          } | null;
          listingUris?: {
              detail?: string | null;
          } | null;
          features?:
              | ({
                    iconId?: string | null;
                    content?: number | null;
                } | null)[]
              | null;
          image?: {
              src?: string | null;
              responsiveImgList?:
                  | ({ src?: string | null; width?: number | null } | null)[]
                  | null;
          } | null;
      }
    | InternalPromoId;

export type FeaturedItem = (
    | { type: 'Internal Promo'; id: PromoIds }
    | { type: 'Featured Property'; data: Product }
) & { onClick: () => void };

export type Props = {
    featuredItems: FeaturedItem[];
    sectionType: 'sale' | 'rent';
    searchParam?: string;
    searchIdentifier: string;
} & React.HTMLProps<HTMLDivElement>;

function FeaturedPropertiesComponent({
    featuredItems,
    sectionType,
    searchParam,
    searchIdentifier
}: Props) {
    const currentSection = sectionType === 'sale' ? 'for-sale' : 'to-rent';

    return featuredItems.length > 0 ? (
        <PropertyWrapper data-testid="featured-listings">
            <StyledFeaturePropertiesHeader data-testid="featured-properties-badge">
                <StyledBadge variant="featured" />
            </StyledFeaturePropertiesHeader>
            {featuredItems.map((item) => {
                if (
                    item.type === 'Featured Property' &&
                    typeof item.data !== 'string'
                ) {
                    return (
                        <FeaturedPropertyCard
                            property={item.data}
                            key={item.data.listingId}
                            onClick={item.onClick}
                            searchIdentifier={searchIdentifier}
                        />
                    );
                }

                if (item.type === 'Internal Promo') {
                    return (
                        <InternalPromoCard
                            promo={internalPromos[item.id]}
                            key={item.id}
                            currentSection={currentSection}
                            searchParam={searchParam}
                            onClick={item.onClick}
                        />
                    );
                }

                return null;
            })}
        </PropertyWrapper>
    ) : null;
}

/**
 * - This component is displayed on the Search Results Page, the component is used to display 1 weekly featured listing and 2 featured listings.
 * - We do not display this component on Mobile devices / sizes. ([Decision log](https://zpgltd.atlassian.net/wiki/spaces/PT/pages/2044231693/Featured+Properties+Availability+on+mobile))
 *
| **Team**               |       |
| -----------------------|--------------|
| **Zeroheight**         |   https://zeroheight.com/8f6f2b33a/v/latest/p/07d92d-listing-cards/b/346990      |
**/
const FeaturedProperties = styled(FeaturedPropertiesComponent)();

export { internalPromoIds };
export default FeaturedProperties;
